/**
 * Tutorial controller. Activated by the TUTO button on the home hub
 * (see `lobby_browser.ts → startTutorial`) and consumed by the game
 * mount (`in_game.ts → mountInGame`).
 *
 * Architecture:
 *
 *   • Module-level state holds the "active tutorial" flag + per-step
 *     cursor. Survives a page navigation inside the SPA, but cleared
 *     by `clearTutorial()` when the player finishes or quits.
 *
 *   • `attachTutorialOverlay(ctx)` mounts a fixed overlay (talking
 *     head + speech bubble + optional spotlight ring) and starts
 *     driving the step list. Each step has a text line + an "advance
 *     condition" (either a state predicate or "user did X"). The
 *     overlay never advances on its own time — it always waits for
 *     the player's action, so the tutorial can't run ahead.
 *
 *   • Voice + typewriter live in the overlay itself; `speakChar` from
 *     `tutorial_voice.ts` is fired for each visible char so the
 *     player hears a chipmunk-cat chirp as the line types out.
 */

import type { LobbyState, Participant, Room } from '../lobby/colyseus.js';
import { speakChar } from '../sound/tutorial_voice.js';
import { auth } from '../auth/state.js';

// ----------------------------------------------------------------------------
// Module-level state

interface TutorialSession {
  /** The faction picked for the local player (favourite or random). */
  playerFactionId: string;
  /** The faction picked for the lone AI opponent. */
  botFactionId: string;
}

let session: TutorialSession | null = null;
/** Set true while the overlay is mounted (so duplicate mounts are no-ops). */
let overlayMounted = false;

/** Called from the hub before the tutorial room is created. */
export function markTutorialActive(opts: TutorialSession): void {
  session = { ...opts };
}

/** Read the active session — null when no tutorial is running. */
export function getTutorialSession(): TutorialSession | null {
  return session;
}

/** Wipe the tutorial state — called when the player closes the
 *  overlay or quits the tutorial room. */
export function clearTutorial(): void {
  session = null;
}

// ----------------------------------------------------------------------------
// Step engine

interface InGameCtxLike {
  room: Room;
  state: LobbyState;
}

interface StepResult {
  /** True if the player has done what this step asked. The engine then
   *  advances to the next step. */
  done: boolean;
}

/**
 * Runtime returned by predicate-mode steps when the engine enters them.
 *   - `check` is polled every tick until it reports done.
 *   - `cleanup` (optional) is called when the step exits — used by
 *     event-subscribed steps to drop their `room.onMessage` handler.
 */
interface StepRuntime {
  check: (ctx: InGameCtxLike, me: Participant | null) => StepResult;
  cleanup?: () => void;
}

interface TutorialStep {
  /** Short slug used in console logs / debugging. */
  id: string;
  /** Bubble line (typewriter target). Cat-themed + funny per spec. */
  text: string;
  /**
   * Condition for advancing. Two modes:
   *   • 'click'   → show a "Continuer" button inside the bubble. The
   *                 player clicks it to move on. Used for intro /
   *                 pure narrative beats.
   *   • factory   → called with the live ctx when the step opens.
   *                 Returns `{ check, cleanup }`. `check` is polled
   *                 every tick — when it reports `done`, advance.
   *                 `cleanup` (if provided) runs on step exit so
   *                 message subscribers can detach cleanly. The
   *                 factory pattern lets each step entry capture a
   *                 fresh "starting snapshot" (e.g. regions owned)
   *                 without smuggling module-level state around.
   */
  advance: 'click' | ((ctx: InGameCtxLike, me: Participant | null) => StepRuntime);
  /** Optional CSS selector to spotlight while this step is active. */
  spotlightSelector?: string;
}

/** Walk participants to find the local player's seat index — used by
 *  several predicates below. -1 if not found. */
function findMyIndex(ctx: InGameCtxLike, me: Participant | null): number {
  if (!me) return -1;
  for (let i = 0; i < ctx.state.participants.length; i++) {
    if (ctx.state.participants[i] === me) return i;
  }
  return -1;
}

function countRegionsOwnedBy(ctx: InGameCtxLike, idx: number): number {
  let n = 0;
  for (let r = 0; r < ctx.state.regionOwners.length; r++) {
    if (ctx.state.regionOwners[r] === idx) n++;
  }
  return n;
}

function countBuildingsOwnedBy(ctx: InGameCtxLike, idx: number, kind: number): number {
  let n = 0;
  for (let r = 0; r < ctx.state.regionBuilding.length; r++) {
    if (
      (ctx.state.regionBuilding[r] ?? 0) === kind &&
      ctx.state.regionOwners[r] === idx
    ) n++;
  }
  return n;
}

/** Subscribe to a Colyseus room message. Returns the unsubscribe fn
 *  the server's room.onMessage gives back. Wrapped here so each
 *  predicate-step factory can plug into events without importing
 *  Colyseus types directly. */
function onMessage(
  ctx: InGameCtxLike,
  type: string,
  handler: (payload: unknown) => void,
): () => void {
  const room = ctx.room as unknown as {
    onMessage(t: string, fn: (p: unknown) => void): () => void;
  };
  return room.onMessage(type, handler);
}

/** Building-tag constants — mirrored from server lobby_room.ts. */
const BUILDING_NONE = 0;
const BUILDING_CAMP = 1;

const STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    text:
      'Miaou ! Je suis ton guide moustachu. On va apprendre, patte par patte.',
    advance: 'click',
  },
  {
    id: 'goal',
    text:
      'BUT du jeu : occire tous les autres chatons pour gagner la War of Lyanx.',
    advance: 'click',
  },
  {
    id: 'dice',
    text:
      'Tirage des moustaches : clique le dé qui pulse. Plus haut = tu joues tôt.',
    advance: () => ({
      check: (_ctx, me) => ({ done: !!me && me.diceRoll > 0 }),
    }),
    spotlightSelector: '.dice-throw-wrap.dice-clickable',
  },
  {
    id: 'dice-wait',
    text: 'Beau lancer ! On attend les autres chats.',
    advance: () => ({
      check: (ctx) => ({ done: ctx.state.phase === 'placement' }),
    }),
  },
  {
    id: 'placement',
    text:
      'Pose ton REPAIRE : clique un grand coin de jardin bien gras.',
    advance: () => ({
      check: (ctx, me) => {
        const myIdx = findMyIndex(ctx, me);
        if (myIdx < 0) return { done: false };
        return { done: (ctx.state.castles[myIdx] ?? -1) !== -1 };
      },
    }),
    spotlightSelector: '.gh-tip',
  },
  {
    id: 'placement-wait',
    text: 'Les autres chats posent leur repaire. Patience.',
    advance: () => ({
      check: (ctx) => ({ done: ctx.state.phase === 'playing' }),
    }),
  },
  {
    id: 'first-turn',
    text:
      'Tes croquettes sont dans la BARRE EN HAUT : {wood} bois, {stone} pierre, {gold} or, {iron} fer, {silver} argent, {fruit} fruits.',
    advance: 'click',
  },
  {
    id: 'continent-bonus',
    text:
      'BON À SAVOIR : pour avoir du {iron} fer et de l\'{silver} argent, il faut capturer un CONTINENT ENTIER.',
    advance: 'click',
  },
  {
    id: 'defend-castle',
    text:
      'RÈGLE D\'OR : ne laisse jamais ton repaire vide — un méchant chaton pourrait le piquer en deux coups de patte. Garde toujours des chats à la maison.',
    advance: 'click',
  },
  {
    id: 'move',
    text:
      'Bouger : clique une meute, puis un territoire VOISIN. Capture un nouveau territoire !',
    advance: (ctx, me) => {
      const myIdx = findMyIndex(ctx, me);
      const start = myIdx >= 0 ? countRegionsOwnedBy(ctx, myIdx) : 0;
      return {
        check: (cctx, cme) => {
          const idx = findMyIndex(cctx, cme);
          if (idx < 0) return { done: false };
          return { done: countRegionsOwnedBy(cctx, idx) > start };
        },
      };
    },
  },
  {
    id: 'reinforce',
    text:
      'RENFORCER : bouger une meute sur ton PROPRE territoire fusionne les deux troupes — utile pour blinder une frontière.',
    advance: 'click',
  },
  {
    id: 'combat-info',
    text:
      'Combat : un panneau s\'affiche AVANT le clash → tu vois les morts + survivants des deux côtés. Pas de surprise.',
    advance: 'click',
  },
  {
    id: 'drops',
    text:
      'Tous les 5 tours, une {snacalia} snacalia dorée tombe du ciel sur un territoire libre. Bouge une meute dessus pour la ramasser direct — gros bonus de croquettes !',
    advance: 'click',
  },
  {
    id: 'missions',
    text:
      'Ah miaou, j\'oubliais ! Tu as une CARTE MISSION en cours : capturer 5 territoires, garder un continent, gagner un combat… Réussis-la pour empocher du bonus.',
    advance: 'click',
  },
  {
    id: 'missions-cooldown',
    text:
      'Petit détail : après chaque mission accomplie, tu attends 3 patrouilles avant la suivante. Le temps de te lécher les pattes.',
    advance: 'click',
  },
  {
    id: 'end-turn-first',
    text:
      'Termine ton tour avec "Fin du tour" en HAUT à droite. Tes coffres se remplissent au prochain.',
    advance: (ctx, me) => {
      const baseTurnNumber = ctx.state.turnNumber;
      return {
        check: (cctx, cme) => {
          if (!cme) return { done: false };
          const movedOn = cme.turnOrder !== cctx.state.currentTurnIndex;
          const turnAdvanced = cctx.state.turnNumber > baseTurnNumber;
          return { done: movedOn || turnAdvanced };
        },
      };
    },
    spotlightSelector: '.gh-end-turn',
  },
  {
    // Phase 1 — get the panel open. The next step (recruit-explain)
    // takes over with a long chatty explanation as soon as the
    // recruit modal is detected in the DOM.
    id: 'recruit-open',
    text:
      'Clique sur ton REPAIRE puis sur "Rameuter la meute" — j\'arrive te ' +
      'présenter le panneau dès qu\'il s\'ouvre.',
    advance: () => ({
      check: () => ({
        done: !!document.querySelector('.recruit-card'),
      }),
    }),
  },
  {
    // Phase 2a — quick welcome to the panel while it's open above us.
    id: 'recruit-explain-mix',
    text:
      'Tadaaa ! Voici TA LISTE DE CHATS. Avec les +/− tu choisis combien de chaque type partent en meute — et tu peux EN MÉLANGER plusieurs d\'un coup, c\'est toi le chef de meute.',
    advance: 'click',
  },
  {
    id: 'recruit-explain-cost',
    text:
      'Chaque chat coûte sa poignée de croquettes ({wood} {stone} {gold} {iron} {silver}) — tu vois le total tomber en bas du panneau au fur et à mesure que tu ajoutes des chats.',
    advance: 'click',
  },
  {
    id: 'recruit-explain-locked',
    text:
      'Les Aristochats et la Légende sont grisés ? Normal, ils sont enfermés à double tour — il faudra sniffer les bonnes idées pour les libérer plus tard.',
    advance: 'click',
  },
  {
    // Phase 2b — actual gating. Player must include at least one
    // ranged unit (archer or crossbowman) so the next "ranged" step
    // has units to fire with.
    id: 'recruit-explain-action',
    text:
      'Allez, glisse AU MOINS UN pêcheur d\'oiseaux dans ta meute (on en aura besoin juste après pour le tir à distance) et valide. MIAOU !',
    advance: (ctx, me) => {
      const myIdx = findMyIndex(ctx, me);
      const rangedTotal = (): number => {
        if (myIdx < 0) return 0;
        let total = 0;
        for (let r = 0; r < ctx.state.regionOwners.length; r++) {
          if (ctx.state.regionOwners[r] !== myIdx) continue;
          const u = ctx.state.regionUnits[r];
          if (!u) continue;
          total += u.archers + u.crossbowmen;
        }
        return total;
      };
      const start = rangedTotal();
      return {
        check: () => ({ done: rangedTotal() > start }),
      };
    },
  },
  {
    id: 'ranged',
    text:
      'Pêcheurs d\'oiseaux : portée 2 PILE (pas adjacent, pas plus). Si l\'ennemi est trop loin, déplace-toi d\'abord à 2 territoires de lui, puis tire !',
    advance: (ctx) => {
      let fired = false;
      const off = onMessage(ctx, 'action.ranged', () => { fired = true; });
      return {
        check: () => ({ done: fired }),
        cleanup: off,
      };
    },
  },
  {
    id: 'camp',
    text:
      'Coin secret : clique un territoire ami libre → "Creuser un coin secret". Coûte du {wood} bois + {stone} pierre. Tu pourras rameuter dessus aussi.',
    advance: (ctx, me) => {
      const myIdx = findMyIndex(ctx, me);
      const start = myIdx >= 0 ? countBuildingsOwnedBy(ctx, myIdx, BUILDING_CAMP) : 0;
      return {
        check: (cctx, cme) => {
          const idx = findMyIndex(cctx, cme);
          if (idx < 0) return { done: false };
          return { done: countBuildingsOwnedBy(cctx, idx, BUILDING_CAMP) > start };
        },
      };
    },
  },
  {
    // Phase 1 — open the tech tree. Spotlight points at the Sniffer
    // button. The next step starts as soon as the modal is in the DOM.
    id: 'tech-open',
    text:
      'Clique "Sniffer" en haut à droite — j\'arrive te déballer l\'arbre dès ' +
      'qu\'il s\'ouvre.',
    advance: () => ({
      check: () => ({
        done: !!document.querySelector('.tech-modal'),
      }),
    }),
    spotlightSelector: '.gh-resbar-research',
  },
  {
    // Phase 2a — bird's-eye view of the tree.
    id: 'tech-explain-intro',
    text:
      'Tadaaa ! Voici l\'arbre des grandes idées. Chaque carte est une amélioration PERMANENTE pour ton royaume — palissades pour mieux défendre, atelier pour tirer plus loin, marché pour plus d\'or…',
    advance: 'click',
  },
  {
    id: 'tech-explain-cost',
    text:
      'Chaque idée coûte des {fruit} fruits — tu vois ta réserve dans la barre du haut. Pas assez ? Fais quelques tours et reviens grignoter.',
    advance: 'click',
  },
  {
    id: 'tech-explain-locked',
    text:
      'Une carte verrouillée ? Lis son pied : il te manque parfois un nombre de territoires, un repaire d\'un niveau donné, ou une autre idée à sniffer avant. Les cartes débloquées te laissent aussi rameuter Aristochats et Légende, alors fonce !',
    advance: 'click',
  },
  {
    // Phase 2b — actual gating.
    id: 'tech-explain-action',
    text:
      'Choisis une carte qui N\'EST PAS grisée et clique "Sniffer !" pour valider. On continue dès que ça pétille.',
    advance: (ctx, me) => {
      const start = me ? me.researchedTechs.length : 0;
      return {
        check: (_cctx, cme) => ({ done: !!cme && cme.researchedTechs.length > start }),
      };
    },
  },
  {
    id: 'done',
    text:
      'Et voilà chaton, tu sais tout ! Maintenant : occire tout le monde et triompher. Bonne chasse, miaou !',
    advance: 'click',
  },
];

// ----------------------------------------------------------------------------
// Overlay mount

/** Refs to live DOM nodes — cleared by `detachTutorialOverlay`. */
interface OverlayRefs {
  root: HTMLDivElement;
  bubble: HTMLDivElement;
  bubbleText: HTMLDivElement;
  continueBtn: HTMLButtonElement;
  head: HTMLImageElement;
  spotlight: HTMLDivElement;
}

let refs: OverlayRefs | null = null;
let stepIndex = 0;
let pollHandle: number | null = null;
/** Token bumped on every typewriter run so previous runs self-cancel
 *  when a new step starts. */
let typewriterToken = 0;
/** Ref to the active game ctx for predicates to read. */
let currentCtx: InGameCtxLike | null = null;
/** Runtime returned by the current factory-mode step. Held across
 *  ticks so we can poll `check` and call `cleanup` on exit. */
let activeRuntime: StepRuntime | null = null;

/**
 * Mount the tutorial overlay into the document. Idempotent. Called
 * by `mountInGame` when a tutorial session is active.
 */
export function attachTutorialOverlay(ctx: InGameCtxLike): void {
  if (!session) return;
  if (overlayMounted) {
    // Re-binding the same ctx so predicates keep working after a
    // state-driven re-mount.
    currentCtx = ctx;
    return;
  }
  overlayMounted = true;
  currentCtx = ctx;
  stepIndex = 0;
  refs = buildOverlay();
  document.body.appendChild(refs.root);
  // Per-frame poll for predicate-driven steps. Cheap — single
  // function call every 120 ms.
  pollHandle = window.setInterval(tickStep, 120);
  // Start the first step.
  runCurrentStep();
}

/** Tear down the overlay + cancel polling. Safe to call when nothing
 *  is mounted (no-ops).
 *
 *  IMPORTANT: this does NOT clear the tutorial session. The cleanup
 *  hook in main.ts fires `unmountInGame` → `detachTutorialOverlay`
 *  even on the initial navigation INTO the tutorial room (router
 *  defensively unmounts before mounting the next screen). If we
 *  wiped the session here, `mountInGame` later would see no session
 *  and the overlay would never appear. The session is cleared
 *  explicitly when the player actually leaves the room (room.onLeave
 *  in tutorial setup) or finishes the last step. */
export function detachTutorialOverlay(): void {
  if (pollHandle !== null) {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
  if (activeRuntime?.cleanup) {
    try { activeRuntime.cleanup(); } catch { /* swallow */ }
  }
  activeRuntime = null;
  if (refs?.root.parentNode) refs.root.remove();
  refs = null;
  overlayMounted = false;
  currentCtx = null;
  typewriterToken++;
}

// ----------------------------------------------------------------------------
// Internals

function buildOverlay(): OverlayRefs {
  const root = document.createElement('div');
  root.className = 'tutorial-overlay';

  // Spotlight container — moved + sized inline at every tick by
  // syncSpotlight() to match the highlighted element's bounding box.
  // Hidden by default (no spotlightSelector on the step). The "ring"
  // is no longer a dashed border — too in-your-face. Instead the
  // container holds three cat-head sprites (crier / neutre / interrogation)
  // that float around the target's perimeter with subtle bob + tilt.
  // Player attention is drawn by the moving cats without any heavy
  // outline obscuring the highlighted control underneath.
  const spotlight = document.createElement('div');
  spotlight.className = 'tutorial-spotlight';
  spotlight.style.display = 'none';
  const addCat = (name: string, slot: string): void => {
    const img = document.createElement('img');
    img.className = `tutorial-spotlight-cat tutorial-spotlight-cat-${slot}`;
    img.src = `/sprites/iconchat_${name}.png`;
    img.alt = '';
    spotlight.appendChild(img);
  };
  addCat('crier', 'top');         // above the target — biggest, screams "look!"
  addCat('neutre', 'left');       // left side — calmer bob
  addCat('interrogation', 'right'); // right side — questioning tilt
  root.appendChild(spotlight);

  // Bottom-left panel: talking head + bubble side by side.
  const panel = document.createElement('div');
  panel.className = 'tutorial-panel';

  const headWrap = document.createElement('div');
  headWrap.className = 'tutorial-head-wrap';
  const head = document.createElement('img');
  head.className = 'tutorial-head';
  head.alt = 'Guide chat';
  // Source set once we know the player's faction.
  const facId = session?.playerFactionId;
  if (facId) head.src = `/sprites/${facId}_army.png`;
  headWrap.appendChild(head);
  panel.appendChild(headWrap);

  const bubble = document.createElement('div');
  bubble.className = 'tutorial-bubble';
  const bubbleText = document.createElement('div');
  bubbleText.className = 'tutorial-bubble-text';
  bubble.appendChild(bubbleText);
  const continueBtn = document.createElement('button');
  continueBtn.type = 'button';
  continueBtn.className = 'tutorial-bubble-btn';
  continueBtn.textContent = 'Continuer';
  continueBtn.style.display = 'none';
  continueBtn.addEventListener('click', () => {
    // Skip ahead immediately, even if the typewriter is still running.
    advanceStep();
  });
  bubble.appendChild(continueBtn);
  panel.appendChild(bubble);

  root.appendChild(panel);
  return { root, bubble, bubbleText, continueBtn, head, spotlight };
}

function runCurrentStep(): void {
  if (!refs) return;
  const step = STEPS[stepIndex];
  if (!step) {
    // End of tutorial — clear the session AND tear down the overlay.
    // Order matters: clear first so any concurrent re-mount attempt
    // (unlikely but possible) sees no active session and bails.
    clearTutorial();
    detachTutorialOverlay();
    return;
  }
  // Reset bubble UI + start typing.
  refs.continueBtn.style.display = 'none';
  startTypewriter(step.text, () => {
    // Once the text is fully revealed, reveal the Continuer button
    // ONLY for click-gated steps. Predicate-gated steps stay open
    // until the condition fires.
    if (step.advance === 'click') {
      refs!.continueBtn.style.display = '';
    }
  });
  // Spawn the predicate-mode runtime ONCE per step entry so each
  // step gets a fresh snapshot (regions owned, message subscribers…).
  activeRuntime = null;
  if (typeof step.advance === 'function' && currentCtx) {
    const me = findLocalParticipant(currentCtx);
    activeRuntime = step.advance(currentCtx, me);
  }
  syncSpotlight();
}

function advanceStep(): void {
  // Tear down the previous step's predicate runtime (drops any
  // room.onMessage subscriber it set up).
  if (activeRuntime?.cleanup) {
    try { activeRuntime.cleanup(); } catch { /* swallow */ }
  }
  activeRuntime = null;
  stepIndex++;
  typewriterToken++; // cancel any in-flight typewriter
  runCurrentStep();
}

function tickStep(): void {
  if (!refs || !currentCtx) return;
  syncSpotlight();
  if (activeRuntime) {
    const me = findLocalParticipant(currentCtx);
    const r = activeRuntime.check(currentCtx, me);
    if (r.done) advanceStep();
  }
}

function findLocalParticipant(ctx: InGameCtxLike): Participant | null {
  // Local player's userId == auth session sub, which is the same id
  // populated on participant.id server-side from the auth cookie.
  const userId = auth.user?.id;
  if (!userId) return null;
  for (let i = 0; i < ctx.state.participants.length; i++) {
    const p = ctx.state.participants[i];
    if (p && p.id === userId) return p;
  }
  return null;
}

function syncSpotlight(): void {
  if (!refs) return;
  const step = STEPS[stepIndex];
  if (!step || !step.spotlightSelector) {
    refs.spotlight.style.display = 'none';
    return;
  }
  const target = document.querySelector(step.spotlightSelector) as HTMLElement | null;
  if (!target) {
    refs.spotlight.style.display = 'none';
    return;
  }
  const rect = target.getBoundingClientRect();
  refs.spotlight.style.display = '';
  refs.spotlight.style.left = `${rect.left - 8}px`;
  refs.spotlight.style.top = `${rect.top - 8}px`;
  refs.spotlight.style.width = `${rect.width + 16}px`;
  refs.spotlight.style.height = `${rect.height + 16}px`;
}

/**
 * Inline-sprite tokens supported inside a step's text. Each token is
 * replaced by an actual <img> at the moment the typewriter "reaches"
 * its position — so when the player reads "tu dépenses des {wood}" the
 * little wood-resource icon pops in at the spot where {wood} sat.
 *
 * Bigger pictograms (snacalia dorée) get a slightly larger sizing in
 * the consuming code (handled per-token via the `large` class).
 */
const BUBBLE_ICONS: Record<string, string> = {
  '{wood}':     '/sprites/ressource_bois.png',
  '{stone}':    '/sprites/ressource_pierre.png',
  '{gold}':     '/sprites/piece_or.png',
  '{iron}':     '/sprites/ressource_fer.png',
  '{silver}':   '/sprites/piece_argent.png',
  '{fruit}':    '/sprites/ressource_fruit.png',
  '{snacalia}': '/sprites/boisson_snacalia_or.png',
  '{castle}':   '/sprites/chateau_tier1.png',
  '{camp}':     '/sprites/camp.png',
};

interface BubbleToken {
  kind: 'text' | 'icon';
  value: string; // text content OR sprite src
}

/** Split a step text into alternating text + icon tokens. Unknown
 *  `{...}` placeholders are left as plain text (so a typo is visible
 *  in the bubble rather than swallowed). */
function tokenizeBubbleText(text: string): BubbleToken[] {
  const out: BubbleToken[] = [];
  const re = /\{[a-z]+\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const tok = m[0];
    const src = BUBBLE_ICONS[tok];
    if (!src) continue; // leave unknown placeholders as plain text below
    if (m.index > last) out.push({ kind: 'text', value: text.slice(last, m.index) });
    out.push({ kind: 'icon', value: src });
    last = m.index + tok.length;
  }
  if (last < text.length) out.push({ kind: 'text', value: text.slice(last) });
  return out;
}

function startTypewriter(text: string, onComplete: () => void): void {
  if (!refs) return;
  const myToken = ++typewriterToken;
  refs.bubbleText.textContent = '';
  // Replace bubbleText content with a fresh container — innerHTML wipe
  // is cleaner than a textContent reset when we mix text + <img>.
  refs.bubbleText.innerHTML = '';

  const tokens = tokenizeBubbleText(text);
  let tokIdx = 0;
  let charIdx = 0;
  let currentNode: Text | null = null;

  const tick = (): void => {
    if (typewriterToken !== myToken) return; // cancelled
    if (tokIdx >= tokens.length) { onComplete(); return; }
    const tok = tokens[tokIdx]!;
    if (tok.kind === 'icon') {
      // Insert sprite atomically (no char-by-char).
      const img = document.createElement('img');
      img.className = 'tutorial-bubble-icon';
      img.src = tok.value;
      img.alt = '';
      refs!.bubbleText.appendChild(img);
      currentNode = null;          // next text starts in a fresh node
      tokIdx++;
      // Small pause after an icon — feels like a comma in the speech.
      window.setTimeout(tick, 60);
      return;
    }
    // Text token: reveal one char per tick.
    if (charIdx === 0) {
      currentNode = document.createTextNode('');
      refs!.bubbleText.appendChild(currentNode);
    }
    if (charIdx < tok.value.length) {
      const ch = tok.value.charAt(charIdx);
      currentNode!.data += ch;
      speakChar(ch);
      charIdx++;
      // 26 ms per char ≈ 38 chars/sec — comfortable reading pace.
      window.setTimeout(tick, 26);
    } else {
      tokIdx++;
      charIdx = 0;
      tick();
    }
  };
  tick();
}
